import React from 'react';
import type { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router';
import ActionButton from '@applied/dragonfruit/src/components/ActionButton/ActionButton';
import Icon from '@applied/dragonfruit/src/components/Icon/Icon';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import Modal from '@applied/dragonfruit/src/components/Modal/Modal';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import TextField from '@applied/dragonfruit/src/components/TextField/TextField';
import { setCookie } from '@applied/utils/cookies';
import { LAST_ACTIVITY_TIME_KEY } from '@applied/utils/dom';
import { isHostnameIpAddress } from '@applied/utils/ip_addresses';
import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport';
import classNames from 'classnames';
import * as qs from 'query-string';

import './login.scss';

import Logo from './assets/logo_applied.svg';

import { DomainAuthorization } from './@applied/proto/adp/services/edge_auth/proto/auth_metadata_service_pb';
import { LoginPageLoginMethod } from './@applied/proto/web_services/accounts/proto/authentication_pb';
import LoginPageBackgroundMobile from './assets/login_page_background_mobile.mp4';
import LoginPageBackground from './assets/login_page_background.mp4';
import { type AppliedGoogleLoginProps, AppliedGoogleLogin } from './AppliedGoogleLogin';
import { getAuthMetadataClient } from './auth_client';
import type { ErrorKey } from './error';
import { ERROR_MESSAGES } from './error';
import { postData } from './postData';

const SSO_LOGIN = 'Login with Custom SSO provider';
const SSO_REGISTER = 'Sign up with Custom SSO provider';

const LOGIN_MOBILE_WIDTH = 512;
const DEFAULT_LOCAL_ACCOUNTS_PORT = '10064';
const SSO_EMAIL_STORAGE_KEY = 'adp_custom_sso_email';

function getRememberedSSOEmail(): string | null {
  try {
    return window.localStorage.getItem(SSO_EMAIL_STORAGE_KEY);
  } catch (error) {
    console.warn('Unable to read remembered SSO email from localStorage', error);
    return null;
  }
}

function rememberSSOEmail(email: string): void {
  try {
    window.localStorage.setItem(SSO_EMAIL_STORAGE_KEY, email);
  } catch (error) {
    console.warn('Unable to save remembered SSO email to localStorage', error);
  }
}

/**
 * Google OAuth returns a JWT (base64url). Extract the email from its payload.
 * Decode base64url safely.
 */
export function getEmailFromGoogleCredential(credential: string): string {
  try {
    // reference: https://developers.google.com/identity/gsi/web/guides/display-google-one-tap#credential_response
    const base64Url = credential.split('.')[1] ?? '';
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );
    const json = JSON.parse(jsonPayload);
    return json.email ?? '';
  } catch (error) {
    console.error('Failed to parse Google credential', error);
    return '';
  }
}

function isLoginMethodAllowed(
  method: LoginPageLoginMethod,
  location: RouteComponentProps['location'],
  local_accounts_host: string,
): boolean {
  // Only applied login is allowed for local accounts flow
  if (local_accounts_host && method !== LoginPageLoginMethod.APPLIED) {
    return false;
  }
  const query = qs.parse(location.search);
  const loginMethods = query.login_method;
  if (typeof loginMethods === 'string') {
    return loginMethods === method.toString();
  }
  if (Array.isArray(loginMethods)) {
    return loginMethods.includes(method.toString());
  }

  // Login methods weren't specified or were misformatted, so allow all login methods.
  return true;
}

// Changing the values of this enum might break links to this page, as they're used as query
// params in the URL.
export enum LoginView {
  LOGIN = 'login',
  RESET_PASSWORD = 'resetPassword',
  REGISTER = 'register',
}

enum AccountsPath {
  LOGIN_LEGACY = 'v2/login',
  RESET_PASSWORD = 'resetPasswordRequest',
  REGISTER_LEGACY = 'register',
  LOGIN = 'v2/auth/login',
  REGISTER = 'auth/register',
  SSO = 'auth/login/sso',
}

// Needs to match the values in the account_utils module
// used by the accounts service.
enum LoginMethod {
  GOOGLE = 'GOOGLE',
  SAML = 'SAML',
  OIDC = 'OIDC',
}

function loginViewFromString(view: string): LoginView | undefined {
  for (const [_key, value] of Object.entries(LoginView)) {
    if (view.toUpperCase() === value.toUpperCase()) {
      return value;
    }
  }

  return undefined;
}

function domainFromEmail(email: string): string {
  const split = email.split('@');
  return split[split.length - 1];
}

type SSOLoginMethod = LoginMethod.SAML | LoginMethod.OIDC;
type SSOEmailFocusEvent = React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>;

type LoginRegisterProps = RouteComponentProps;

interface LoginRegisterState {
  pageWidth: number;
  errorMessage: string;
  successMessage: string;
  loginEmail: string;
  loginPassword: string;
  rFirst: string;
  rLast: string;
  rEmail: string;
  rPass: string;
  rConfirmPass: string;
  loginRequested: boolean;
  registerRequested: boolean;
  resetPasswordRequested: boolean;
  googleButtonDisabled: boolean;
  showSSOModal: boolean;
  emailSSO: string;
  selectSSOEmailOnFocus: boolean;
  home_page_banner: string;
  local_accounts_host: string;
  local_accounts_port: string;
  local_accounts_v2_enabled: boolean;
  allowed_orgs: string[];
  resource_type: string;
  cluster_name: string;
  hide_secondary_links: boolean;
  page_title: string;
  disable_video: boolean;
}

// eslint-disable-next-line functional/no-classes
class LoginRegister extends React.PureComponent<LoginRegisterProps, LoginRegisterState> {
  override state: LoginRegisterState = {
    pageWidth: undefined! /* Fixme: http://tiny.cc/r4g9vz */,
    errorMessage: '',
    successMessage: '',
    loginEmail: '',
    loginPassword: '',
    rFirst: '',
    rLast: '',
    rEmail: '',
    rPass: '',
    rConfirmPass: '',
    loginRequested: false,
    registerRequested: false,
    resetPasswordRequested: false,
    googleButtonDisabled: false,
    showSSOModal: false,
    emailSSO: '',
    selectSSOEmailOnFocus: false,
    home_page_banner: '',
    local_accounts_host: '',
    local_accounts_port: '',
    local_accounts_v2_enabled: false,
    allowed_orgs: [],
    resource_type: '',
    cluster_name: '',
    hide_secondary_links: false,
    page_title: '',
    disable_video: false,
  };

  getAccountsUrl = (path: string): string => {
    const { local_accounts_v2_enabled } = this.state;
    const baseUrl = local_accounts_v2_enabled
      ? window.location.origin + '/accounts'
      : __ACCOUNTS_URL__;
    if (path.startsWith('/')) {
      return `${baseUrl}${path}`;
    }
    return `${baseUrl}/${path}`;
  };

  override componentDidMount(): void {
    const { location } = this.props;
    // Redirected here from the SSO login/register flow, try to login/register
    // to Applied immediately.
    const parsed = qs.parse(location.search);

    // Parse all query params first
    let allowed_orgs = [] as string[];
    if (parsed.allowed_org !== undefined) {
      if (typeof parsed.allowed_org === 'string') {
        allowed_orgs = [parsed.allowed_org] as string[];
      } else if (Array.isArray(parsed.allowed_org)) {
        allowed_orgs = parsed.allowed_org as string[];
      }
    }

    const stateUpdates: Record<string, unknown> = {
      allowed_orgs,
    };

    if (parsed.resource_type !== undefined) {
      stateUpdates.resource_type = parsed.resource_type as string;
    }
    if (parsed.cluster_name !== undefined) {
      stateUpdates.cluster_name = parsed.cluster_name as string;
    }
    if (parsed.home_page_banner !== undefined) {
      stateUpdates.home_page_banner = parsed.home_page_banner as string;
    }
    if (parsed.local_accounts_host !== undefined) {
      stateUpdates.local_accounts_host = parsed.local_accounts_host as string;
    }
    if (parsed.local_accounts_port !== undefined) {
      stateUpdates.local_accounts_port = parsed.local_accounts_port as string;
    } else {
      stateUpdates.local_accounts_port = DEFAULT_LOCAL_ACCOUNTS_PORT;
    }
    if (parsed.local_accounts_v2_enabled !== undefined) {
      stateUpdates.local_accounts_v2_enabled = parsed.local_accounts_v2_enabled === 'true';
    }
    if (parsed.error !== undefined && typeof parsed.error === 'string') {
      const errorKey = parsed.error;
      stateUpdates.errorMessage =
        errorKey && errorKey in ERROR_MESSAGES ? ERROR_MESSAGES[errorKey as ErrorKey] : errorKey;
    }
    if (parsed.hide_secondary_links === 'true') {
      stateUpdates.hide_secondary_links = true;
    }
    if (typeof parsed.page_title === 'string') {
      stateUpdates.page_title = parsed.page_title;
    }
    if (parsed.disable_video === 'true') {
      stateUpdates.disable_video = true;
    }

    // Update all state first, then trigger SSO methods in callback
    this.setState(stateUpdates as unknown as LoginRegisterState, () => {
      if (parsed.saml !== undefined && parsed.email !== undefined) {
        // The login/register values need to match the values of LoginView above
        if (parsed.login !== undefined) {
          this.handleSSOLogin(
            parsed.email as string,
            LoginMethod.SAML,
            parsed.id as string,
            allowed_orgs,
            parsed.resource_type as string,
            parsed.cluster_name as string,
          );
        }
        if (parsed.register !== undefined) {
          this.handleSSORegister(LoginMethod.SAML, parsed.id as string);
        }
      }
      if (parsed.oidc !== undefined && parsed.email !== undefined) {
        // The login/register values need to match the values of LoginView above
        if (parsed.login !== undefined) {
          this.handleSSOLogin(
            parsed.email as string,
            LoginMethod.OIDC,
            parsed.id as string,
            allowed_orgs,
            parsed.resource_type as string,
            parsed.cluster_name as string,
          );
        }
        if (parsed.register !== undefined) {
          this.handleSSORegister(LoginMethod.OIDC, parsed.id as string);
        }
      }
    });

    this.onResize();
    this.determineIfGoogleLoginSupported();
    window.addEventListener('resize', this.onResize);
  }

  override componentWillUnmount(): void {
    window.removeEventListener('resize', this.onResize);
  }

  private determineIfGoogleLoginSupported(): void {
    const hostname = window.location.hostname;
    if (isHostnameIpAddress(hostname)) {
      this.setState({
        googleButtonDisabled: true,
      });
    }
  }

  private updateCurrentViewQueryParam(currentView: LoginView): void {
    const { history } = this.props;
    const search = qs.parse(history.location.search);
    search.view = currentView;
    history.push({
      pathname: history.location.pathname,
      search: qs.stringify(search),
    });
  }

  private getCurrentView(): LoginView {
    const { location } = this.props;
    const search = qs.parse(location.search);
    if (search.view) {
      return loginViewFromString(search.view.toString()) ?? LoginView.LOGIN;
    }

    return LoginView.LOGIN;
  }

  private onResize = (): void => {
    this.setState({ pageWidth: window.innerWidth });
  };

  // Validate whether the provided domain is authorized to view this cluster.
  // In particular, this is used to prevent the FE from attempting to hit another endpoint
  // if it knows it will not be authorized even after authentication.
  checkDomainPreAuthentication = (email: string): Promise<void> => {
    const { location } = this.props;
    const search = qs.parse(location.search);
    // We require a query param to indicate that we support this type of check because
    // this login page is shared by home.applied.co that does not support this.
    if (!search.supports_domain_auth_check) {
      return Promise.resolve();
    }
    if (
      typeof search.supports_domain_auth_check !== 'string' ||
      search.supports_domain_auth_check.toLowerCase() !== 'true'
    ) {
      return Promise.resolve();
    }
    return getAuthMetadataClient()
      .checkDomainAuthorization({
        domain: domainFromEmail(email),
      })
      .then((response) => {
        if (response.status.code !== GrpcStatusCode[GrpcStatusCode.OK]) {
          return Promise.reject({
            displayErrorMessage: ERROR_MESSAGES.DOMAIN_AUTH_CHECK_FAILED,
          });
        }
        if (
          response.response.authorization === DomainAuthorization.DOMAIN_AUTHORIZATION_AUTHORIZED ||
          response.response.authorization === DomainAuthorization.DOMAIN_AUTHORIZATION_UNSPECIFIED
        ) {
          return;
        }
        return Promise.reject({
          displayErrorMessage: ERROR_MESSAGES.DOMAIN_NOT_AUTHORIZED,
        });
      });
  };

  changeView = (event: React.MouseEvent<HTMLSpanElement>): void => {
    const { local_accounts_host } = this.state;
    if (local_accounts_host) {
      this.setState({
        errorMessage: ERROR_MESSAGES.LOCAL_ACCOUNTS_NOT_SUPPORTED,
        successMessage: '',
      });
      return;
    }
    const desiredView = loginViewFromString((event.target as HTMLSpanElement).dataset.value ?? '');
    if (desiredView && Object.values(LoginView).includes(desiredView)) {
      this.setState({ errorMessage: '' });
      this.updateCurrentViewQueryParam(desiredView);
    }
  };

  handleRegister = (event: React.MouseEvent): void => {
    event.preventDefault();
    const { rFirst, rLast, rEmail, rPass, rConfirmPass } = this.state;

    this.setState({ registerRequested: true });

    this.checkDomainPreAuthentication(rEmail)
      .then(() => {
        return postData(this.getAccountsUrl(AccountsPath.REGISTER_LEGACY), {
          first_name: rFirst,
          last_name: rLast,
          email: rEmail,
          password: rPass,
          confirm_password: rConfirmPass,
        });
      })
      .then((data) => {
        this.setState({ registerRequested: false });

        if ('error' in data) {
          this.setState({ successMessage: '', errorMessage: data.error });
          return;
        }

        if ('success' in data) {
          this.setState({ errorMessage: '' });
          this.setState({ successMessage: data.success });
          this.setState({ loginEmail: rEmail });
          this.updateCurrentViewQueryParam(LoginView.LOGIN);
        }
      })
      .catch((genericError) => {
        let displayMessage = ERROR_MESSAGES.GENERIC;
        if (genericError.displayErrorMessage !== undefined) {
          displayMessage = genericError.displayErrorMessage;
        }
        this.setState({
          registerRequested: false,
          errorMessage: displayMessage,
        });
      });
  };

  handleLoginSuccess = (serverResponse: any): void => {
    const { location } = this.props;

    let redirectURL;
    if ('refresh' in serverResponse) {
      this.setState({ errorMessage: '' });
      setCookie('refresh_token', serverResponse.refresh, 14);
    }
    if ('auth' in serverResponse) {
      this.setState({ errorMessage: '' });
      setCookie('auth_token', serverResponse.auth, 14);
      window.localStorage.setItem(LAST_ACTIVITY_TIME_KEY, Date.now().toString());

      const parsed = qs.parse(location.search);

      redirectURL = window.location.origin;
      if (parsed.redirect && typeof parsed.redirect === 'string') {
        redirectURL = new URL(parsed.redirect, document.baseURI).href;
      }

      redirectURL += window.location.hash;
    }
    if (redirectURL) {
      window.location.assign(redirectURL as any);
    }
  };

  handleLogin = (event: React.MouseEvent): void => {
    event.preventDefault();
    const {
      loginEmail,
      loginPassword,
      local_accounts_host,
      local_accounts_port,
      allowed_orgs,
      resource_type,
      cluster_name,
    } = this.state;

    this.setState({ loginRequested: true });

    let accounts_url = this.getAccountsUrl(AccountsPath.LOGIN_LEGACY);
    // Local network authentication flow (e.g. for LDAP customers)
    if (local_accounts_host && local_accounts_port) {
      accounts_url = `${local_accounts_host}:${local_accounts_port}/authenticate`;
    }

    this.checkDomainPreAuthentication(loginEmail)
      .then(() => {
        return postData(accounts_url, {
          email: loginEmail,
          password: loginPassword,
          insecure_allowed_orgs: allowed_orgs,
          resource_type: resource_type,
          cluster_name: cluster_name,
        });
      })
      .then((serverResponse) => {
        this.setState({ loginRequested: false });
        if ('error' in serverResponse) {
          this.setState({ errorMessage: serverResponse.error, successMessage: '' });
          return;
        }
        this.handleLoginSuccess(serverResponse);
      })
      .catch((genericError) => {
        let displayMessage = ERROR_MESSAGES.GENERIC;
        if (genericError.displayErrorMessage !== undefined) {
          displayMessage = genericError.displayErrorMessage;
        }
        this.setState({
          loginRequested: false,
          errorMessage: displayMessage,
        });
      });
  };

  handleResetPassword = (event: React.MouseEvent): void => {
    event.preventDefault();
    const { loginEmail } = this.state;
    this.setState({ resetPasswordRequested: true });
    postData(this.getAccountsUrl(AccountsPath.RESET_PASSWORD), { email: loginEmail })
      .then((data) => {
        this.setState({ resetPasswordRequested: false });
        if ('error' in data) {
          this.setState({ errorMessage: data.error, successMessage: '' });
        }

        if ('success' in data) {
          this.setState({ successMessage: data.success });
          this.updateCurrentViewQueryParam(LoginView.LOGIN);
        }
      })
      .catch(() => {
        this.setState({
          resetPasswordRequested: false,
          errorMessage: ERROR_MESSAGES.GENERIC,
        });
      });
  };

  clickSupport = (): any => {
    const { local_accounts_host } = this.state;
    if (local_accounts_host) {
      this.setState({
        errorMessage: ERROR_MESSAGES.LOCAL_ACCOUNTS_NOT_SUPPORTED,
        successMessage: '',
      });
      return;
    }
    window.open('mailto:support@applied.co');
  };

  responseGoogleLoginSuccess = async (credential: string): Promise<void> => {
    const { allowed_orgs, resource_type, cluster_name } = this.state;
    this.setState({ loginRequested: true });
    if (!credential) {
      this.responseGoogleLoginFailure();
      return;
    }
    const userEmail = getEmailFromGoogleCredential(credential);
    if (!userEmail) {
      this.responseGoogleLoginFailure();
      return;
    }
    try {
      const serverResponse = await postData(
        this.getAccountsUrl(AccountsPath.LOGIN),
        {
          email: userEmail,
          login_method: LoginMethod.GOOGLE,
          insecure_allowed_orgs: allowed_orgs,
          resource_type: resource_type,
          cluster_name: cluster_name,
        },
        { Authorization: `Bearer ${credential}` },
      );
      this.setState({ loginRequested: false });
      if ('error' in serverResponse) {
        this.setState({ errorMessage: serverResponse.error, successMessage: '' });
      }
      this.handleLoginSuccess(serverResponse);
    } catch (error) {
      this.setState({
        resetPasswordRequested: false,
        errorMessage: ERROR_MESSAGES.GOOGLE_LOGIN_FAILED,
      });
    }
  };

  responseGoogleLoginFailure = (): void => {
    // This error happens when the domain is not registered
    // with Google
    this.setState({
      loginRequested: false,
      errorMessage: ERROR_MESSAGES.GOOGLE_LOGIN_FAILED,
    });
  };

  responseGoogleRegisterSuccess = (credential: string): void => {
    this.setState({ registerRequested: true });
    const authHeader = { Authorization: `Bearer ${credential}` };

    postData(
      this.getAccountsUrl(AccountsPath.REGISTER),
      {
        register_method: LoginMethod.GOOGLE,
      },
      authHeader,
    )
      .then((serverResponse) => {
        this.setState({ registerRequested: false });

        if ('error' in serverResponse) {
          this.setState({ errorMessage: serverResponse.error, successMessage: '' });
          return;
        }

        if ('success' in serverResponse) {
          this.setState({
            errorMessage: '',
            successMessage: serverResponse.success,
          });
          this.updateCurrentViewQueryParam(LoginView.LOGIN);
        }
      })
      .catch(() => {
        this.setState({
          resetPasswordRequested: false,
          errorMessage: ERROR_MESSAGES.GOOGLE_REGISTER_FAILED,
        });
      });
  };

  responseGoogleRegisterFailure = (): void => {
    // This error happens when the domain is note registered
    // with Google
    this.setState({
      loginRequested: false,
      errorMessage: ERROR_MESSAGES.GOOGLE_REGISTER_FAILED,
    });
  };

  private updateValue(
    field: keyof LoginRegisterState,
    ev: React.ChangeEvent<HTMLInputElement>,
  ): void {
    const newState = { [field]: ev.target.value };
    this.setState(newState as any);
  }

  initiateSSO = (): void => {
    const { location } = this.props;
    const { emailSSO, allowed_orgs, resource_type, cluster_name } = this.state;

    const parsed = qs.parse(location.search);
    if (emailSSO) {
      rememberSSOEmail(emailSSO);
    }
    this.checkDomainPreAuthentication(emailSSO)
      .then(() => {
        return postData(
          this.getAccountsUrl(AccountsPath.SSO),
          {
            email: emailSSO,
            source: this.getCurrentView(),
            redirect: parsed.redirect,
            insecure_allowed_orgs: allowed_orgs,
            resource_type: resource_type,
            cluster_name: cluster_name,
          },
          undefined /* headers */,
          true /* withCredentials */,
        );
      })
      .then((serverResponse) => {
        if ('error' in serverResponse) {
          this.setState({ errorMessage: serverResponse.error, successMessage: '' });
          return;
        }
        window.location = serverResponse.sso_url;
      })
      .catch((genericError) => {
        let displayMessage = ERROR_MESSAGES.GENERIC;
        if (genericError.displayErrorMessage !== undefined) {
          displayMessage = genericError.displayErrorMessage;
        }
        this.setState({
          errorMessage: displayMessage,
        });
      });
  };

  handleSSOLogin = (
    email: string,
    sso_method: SSOLoginMethod,
    request_id: string | undefined = undefined,
    allowed_orgs: string[],
    resource_type: string,
    cluster_name: string,
  ): void => {
    this.setState({ loginRequested: true });

    postData(
      this.getAccountsUrl(AccountsPath.LOGIN),
      {
        email,
        login_method: sso_method,
        id: request_id,
        insecure_allowed_orgs: allowed_orgs,
        resource_type: resource_type,
        cluster_name: cluster_name,
      },
      undefined /* headers */,
      true /* withCredentials */,
    )
      .then((serverResponse) => {
        this.setState({ loginRequested: false });
        if ('error' in serverResponse) {
          this.setState({ errorMessage: serverResponse.error, successMessage: '' });
          return;
        }
        this.handleLoginSuccess(serverResponse);
      })
      .catch(() => {
        this.setState({
          loginRequested: false,
          errorMessage: ERROR_MESSAGES.GENERIC,
        });
      });
  };

  handleSSORegister = (
    sso_method: SSOLoginMethod,
    request_id: string | undefined = undefined,
  ): void => {
    this.setState({ registerRequested: true });

    postData(
      this.getAccountsUrl(AccountsPath.REGISTER),
      {
        register_method: sso_method,
        id: request_id,
      },
      undefined /* headers */,
      true /* withCredentials */,
    )
      .then((serverResponse) => {
        this.setState({ registerRequested: false });

        if ('error' in serverResponse) {
          this.setState({ errorMessage: serverResponse.error, successMessage: '' });
          return;
        }

        if ('success' in serverResponse) {
          this.setState({
            errorMessage: '',
            successMessage: serverResponse.success,
          });
          this.updateCurrentViewQueryParam(LoginView.LOGIN);
        }
      })
      .catch(() => {
        this.setState({
          resetPasswordRequested: false,
          errorMessage: ERROR_MESSAGES.GENERIC,
        });
      });
  };

  private createGoogleLoginButton(
    context: AppliedGoogleLoginProps['context'],
    onSuccess: AppliedGoogleLoginProps['onSuccess'],
    onError: AppliedGoogleLoginProps['onError'],
  ): JSX.Element {
    return (
      <div className="google-button-wrapper">
        <AppliedGoogleLogin context={context} onSuccess={onSuccess} onError={onError} />
      </div>
    );
  }

  private createSSOButton(
    text: string,
    initiateSSO: () => void,
    toggleModal: (showOrHide: boolean) => void,
    updateEmail: (value: string) => void,
    primaryButtonText: string,
    email?: string,
  ): JSX.Element {
    const { showSSOModal } = this.state;
    return (
      <>
        <ActionButton
          size="large"
          shouldFillContainer
          shouldCenterText
          className="sso-login-button"
          onClick={(): void => toggleModal(true)}
          iconBeforeProps={{ src: 'LOGIN', height: 24 }}
        >
          {text}
        </ActionButton>
        {showSSOModal && (
          <Modal
            onClose={(): void => toggleModal(false)}
            title="SSO Login"
            primaryButton={{
              text: primaryButtonText,
              onClick: initiateSSO,
            }}
            secondaryButton={{ text: 'Cancel' }}
          >
            <TextField
              caption="Email"
              value={email ?? ''}
              onChange={updateEmail}
              onSubmit={initiateSSO}
              focusOnMount
              onFocus={this.handleSSOEmailFocus}
              height="medium"
              shouldFillContainer
            />
          </Modal>
        )}
      </>
    );
  }

  toggleSSOModal = (openOrHide: boolean): void => {
    if (!openOrHide) {
      this.setState({ showSSOModal: false });
      return;
    }

    const { loginEmail, rEmail } = this.state;
    const currentView = this.getCurrentView();
    const rememberedEmail = getRememberedSSOEmail();
    const emailSSO = rememberedEmail || (currentView === LoginView.LOGIN ? loginEmail : rEmail);
    this.setState({
      showSSOModal: true,
      emailSSO,
      selectSSOEmailOnFocus: Boolean(emailSSO),
    });
  };

  handleSSOEmailFocus = (event: SSOEmailFocusEvent): void => {
    const { selectSSOEmailOnFocus } = this.state;
    if (!selectSSOEmailOnFocus) {
      return;
    }

    event.currentTarget.select();
    this.setState({ selectSSOEmailOnFocus: false });
  };

  updateSSOEmail = (value: string): void => {
    this.setState({
      emailSSO: value,
    });
  };

  private createLoginForm(): JSX.Element {
    const { location } = this.props;
    const {
      loginRequested,
      emailSSO,
      home_page_banner,
      local_accounts_host,
      googleButtonDisabled,
      hide_secondary_links,
    } = this.state;

    const appliedLoginAllowed = isLoginMethodAllowed(
      LoginPageLoginMethod.APPLIED,
      location,
      local_accounts_host,
    );
    const googleAllowed = isLoginMethodAllowed(
      LoginPageLoginMethod.GOOGLE,
      location,
      local_accounts_host,
    );
    const ssoAllowed = isLoginMethodAllowed(
      LoginPageLoginMethod.CUSTOM_SSO,
      location,
      local_accounts_host,
    );

    return (
      <form>
        {appliedLoginAllowed && (
          <>
            <input
              type="text"
              placeholder="Email"
              onChange={(ev): void => this.updateValue('loginEmail', ev)}
            />
            <input
              type="password"
              placeholder="Password"
              onChange={(ev): void => this.updateValue('loginPassword', ev)}
            />
            <ActionButton
              size="large"
              shouldFillContainer
              shouldCenterText
              appearance="primary"
              disabled={loginRequested}
              onClick={this.handleLogin}
            >
              Login
            </ActionButton>
          </>
        )}
        {appliedLoginAllowed && (googleAllowed || ssoAllowed) && <div className="subtext">or</div>}
        {googleAllowed &&
          !googleButtonDisabled &&
          this.createGoogleLoginButton(
            'signin_with',
            this.responseGoogleLoginSuccess,
            this.responseGoogleLoginFailure,
          )}
        {ssoAllowed &&
          this.createSSOButton(
            SSO_LOGIN,
            this.initiateSSO,
            this.toggleSSOModal,
            this.updateSSOEmail,
            'Login',
            emailSSO,
          )}
        {!hide_secondary_links && (
          <>
            <div className="subtext large-margin">
              <span className="link" data-value={LoginView.REGISTER} onClick={this.changeView}>
                Sign Up
              </span>
              <span> • </span>
              <span
                className="link"
                data-value={LoginView.RESET_PASSWORD}
                onClick={this.changeView}
              >
                Forgot Password
              </span>
            </div>
            <div className="subtext link" onClick={this.clickSupport}>
              support@applied.co
            </div>
          </>
        )}
        {home_page_banner && <div className="home-page-banner"> {home_page_banner} </div>}
      </form>
    );
  }

  private createRegisterForm(): JSX.Element {
    const { location } = this.props;
    const { registerRequested, emailSSO, local_accounts_host, googleButtonDisabled } = this.state;

    const appliedLoginAllowed = isLoginMethodAllowed(
      LoginPageLoginMethod.APPLIED,
      location,
      local_accounts_host,
    );
    const googleAllowed = isLoginMethodAllowed(
      LoginPageLoginMethod.GOOGLE,
      location,
      local_accounts_host,
    );
    const ssoAllowed = isLoginMethodAllowed(
      LoginPageLoginMethod.CUSTOM_SSO,
      location,
      local_accounts_host,
    );

    return (
      <form>
        {appliedLoginAllowed && (
          <>
            <input
              type="text"
              placeholder="(Optional) First Name"
              onChange={(ev): void => this.updateValue('rFirst', ev)}
            />
            <input
              type="text"
              placeholder="(Optional) Last Name"
              onChange={(ev): void => this.updateValue('rLast', ev)}
            />
            <input
              type="text"
              placeholder="Company Email"
              onChange={(ev): void => this.updateValue('rEmail', ev)}
            />
            <input
              type="password"
              placeholder="Password"
              onChange={(ev): void => this.updateValue('rPass', ev)}
            />
            <input
              type="password"
              placeholder="Confirm Password"
              onChange={(ev): void => this.updateValue('rConfirmPass', ev)}
            />
            <ActionButton
              size="large"
              shouldFillContainer
              shouldCenterText
              appearance="primary"
              disabled={registerRequested}
              onClick={this.handleRegister}
            >
              Sign Up
            </ActionButton>
          </>
        )}
        {appliedLoginAllowed && (googleAllowed || ssoAllowed) && <div className="subtext">or</div>}
        {googleAllowed &&
          !googleButtonDisabled &&
          this.createGoogleLoginButton(
            'signup_with',
            this.responseGoogleRegisterSuccess,
            this.responseGoogleRegisterFailure,
          )}
        {ssoAllowed &&
          this.createSSOButton(
            SSO_REGISTER,
            this.initiateSSO,
            this.toggleSSOModal,
            this.updateSSOEmail,
            'Sign up',
            emailSSO,
          )}
        <div className="subtext large-margin">Already have an account?</div>
        <div className="subtext link" data-value={LoginView.LOGIN} onClick={this.changeView}>
          Login
        </div>
      </form>
    );
  }

  private createPasswordResetForm(): JSX.Element {
    const { resetPasswordRequested } = this.state;

    return (
      <form>
        <input
          type="text"
          placeholder="Email"
          onChange={(ev): void => this.updateValue('loginEmail', ev)}
        />
        <ActionButton
          disabled={resetPasswordRequested}
          onClick={this.handleResetPassword}
          size="large"
          shouldFillContainer
          shouldCenterText
          appearance="primary"
        >
          Send a Password Reset Email
        </ActionButton>
        <div
          className="subtext large-margin link"
          data-value={LoginView.LOGIN}
          onClick={this.changeView}
        >
          Back to Login
        </div>
      </form>
    );
  }

  override render(): JSX.Element {
    const {
      successMessage,
      loginRequested,
      registerRequested,
      resetPasswordRequested,
      pageWidth,
      disable_video,
      page_title,
    } = this.state;
    let { errorMessage } = this.state;

    const currentView = this.getCurrentView();

    if (loginRequested || resetPasswordRequested || registerRequested) {
      errorMessage = '';
    }

    const logoElement = page_title ? null : (
      <Icon src={Logo} className="applied-logo" height="native" />
    );

    return (
      <Flex
        className="login-container"
        alignment="center"
        justify={disable_video ? 'center' : 'flex-end'}
      >
        {disable_video ? (
          <div className="background" />
        ) : (
          <>
            <video
              autoPlay
              loop
              muted
              playsInline
              id="test-desktop-background"
              className={classNames('background', { hidden: pageWidth < LOGIN_MOBILE_WIDTH })}
            >
              <source src={LoginPageBackground} type="video/mp4" />
            </video>
            <video
              autoPlay
              loop
              muted
              playsInline
              id="test-mobile-background"
              className={classNames('background', { hidden: pageWidth >= LOGIN_MOBILE_WIDTH })}
            >
              <source src={LoginPageBackgroundMobile} type="video/mp4" />
            </video>
          </>
        )}
        <div className="login-form-container" style={disable_video ? { width: 'auto' } : undefined}>
          <div className="login-form backdrop-blur">
            {logoElement}
            {page_title && (
              <Typography variant="header" textAlign="center">
                {page_title}
              </Typography>
            )}
            {errorMessage ? (
              <div className="error-message">{errorMessage}</div>
            ) : (
              <div className="success-message">{successMessage}</div>
            )}
            {currentView === LoginView.LOGIN && this.createLoginForm()}
            {currentView === LoginView.REGISTER && this.createRegisterForm()}
            {currentView === LoginView.RESET_PASSWORD && this.createPasswordResetForm()}
          </div>
        </div>
      </Flex>
    );
  }
}

export default withRouter<LoginRegisterProps, typeof LoginRegister>(LoginRegister);
